import { memo } from 'react';

import { type GetByShortIDCollectionResponse } from '@air/api';
import { useShortIdContext } from '@air/provider-short-id';

import { PublicViewVisibilityToggleMenu } from '~/components/ViewControls/ViewVisiblityToggleMenu/PublicViewVisibilityToggleMenu';
import { BoardHeader } from '~/components/v2/components/board/BoardHeader';
import { PublicHeaderTitle } from '~/components/v2/components/shared/PublicHeaderTitle';
import { getPublicShowcasePageTitle } from '~/components/v2/components/showcase/getPublicShowcasePageTitle';
import { usePublicShowcaseCurrentSubPage } from '~/components/v2/components/showcase/hooks/usePublicShowcaseCurrentSubPage';
import { PublicShowcaseHeaderDescription } from '~/components/v2/components/showcase/PublicShowcaseHeaderDescription';
import { ShowcaseHeaderViewControls } from '~/components/v2/components/showcase/ShowcaseHeaderViewControls';

export type PublicShowcaseHeaderProps = {
  collection: Pick<GetByShortIDCollectionResponse['data'], 'name' | 'description'>;
};

export const PublicShowcaseHeader = memo(({ collection }: PublicShowcaseHeaderProps) => {
  // Reading the short-id context HERE rather than inside ShowcaseHeaderViewControls,
  // because that component is also rendered by PrivateShowcaseHeader which has no
  // ShortIdProvider ancestor (useShortIdContext throws outside the provider).
  const { presentationSettings } = useShortIdContext();
  const isExpandNestedDisabled = presentationSettings?.lockedControls?.expandNested === true;

  // Inside a sub-page the header names THAT page; on Home/root the hook returns null and the header
  // falls back to the collection link's own name (unchanged). Canvas-aware, so switching the active
  // page in the editor's Navigation section updates this live. Both names are guaranteed non-empty:
  // the editor's Save gates on a collection name and the sub-page rename input refuses an empty one.
  const currentSubPage = usePublicShowcaseCurrentSubPage();
  const title = getPublicShowcasePageTitle({
    collectionName: collection.name,
    currentSubPageName: currentSubPage?.subPage.name,
  });

  // The header decides which description belongs to the page it is naming, once, and hands the
  // renderer a definite value.
  //
  // Root page: the showcase's own description, exactly as before. Sub-page: NONE. A sub-page has no
  // description of its own to show — `CollectionSubPageOutput` carries no such field — and the
  // showcase's description sitting under a sub-page's title reads as that page's, which it is not
  // (FOUND-533). Giving each page its own description is a backend change; until it lands the honest
  // render is nothing, not the parent's.
  const description = currentSubPage ? null : (collection.description ?? null);

  return (
    <BoardHeader
      title={<PublicHeaderTitle title={title} dataTestId="PUBLIC_COLLECTION_HEADER_TITLE" />}
      description={<PublicShowcaseHeaderDescription description={description} />}
      viewActions={
        <ShowcaseHeaderViewControls
          isExpandNestedDisabled={isExpandNestedDisabled}
          visibilityToggleMenu={<PublicViewVisibilityToggleMenu />}
        />
      }
    />
  );
});

PublicShowcaseHeader.displayName = 'PublicShowcaseHeader';
